import React, { useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import MetricCard from '../components/MetricCard';
import { AIOutlineButton } from '../components/ui/AIOutlineButton';
import { AISidebar } from '../components/AISidebar';
import { AIRecommendationPanel } from '../components/ai/AIRecommendationPanel';
import { Building2, Coins, TrendingDown, Download, Filter } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
// Mock data
const claimsByPartner = [{
  name: 'La Saponaria',
  claims: 234,
  color: '#1a4d2e'
}, {
  name: 'EcoStore',
  claims: 187,
  color: '#2d7a4f'
}, {
  name: 'GreenLife',
  claims: 156,
  color: '#4aa770'
}, {
  name: 'BioMarket',
  claims: 142,
  color: '#7bc99c'
}, {
  name: 'NaturaSì',
  claims: 98,
  color: '#a8ddb5'
}];
const topClaims = [{
  id: 1,
  reward: 'Siero Acido Ialuronico',
  partner: 'La Saponaria',
  partnerId: 'partner-1',
  claims: 234,
  points: 11700
}, {
  id: 2,
  reward: 'Kit Cosmetici Solidi',
  partner: 'La Saponaria',
  partnerId: 'partner-1',
  claims: 187,
  points: 9350
}, {
  id: 3,
  reward: 'Buono Spesa €20',
  partner: 'EcoStore',
  partnerId: 'partner-2',
  claims: 156,
  points: 6240
}, {
  id: 4,
  reward: 'Workshop Sostenibilità',
  partner: 'GreenLife',
  partnerId: 'partner-3',
  claims: 142,
  points: 14200
}, {
  id: 5,
  reward: 'Box Prodotti Bio',
  partner: 'BioMarket',
  partnerId: 'partner-4',
  claims: 98,
  points: 6860
}];
// Mock AI recommendations for marketplace
const getMarketplaceRecommendations = () => [{
  id: '1',
  title: 'Ottimizza la distribuzione dei premi',
  type: 'Opportunità',
  diamondRange: 'La Saponaria: 28% riscatti',
  reason: "La Saponaria genera il 28% dei riscatti totali. Considera di bilanciare l'offerta con altri partner per diversificare l'engagement.",
  ctaLabel: 'Vedi strategie'
}, {
  id: '2',
  title: 'Partner sottoutilizzati',
  type: 'Attenzione',
  diamondRange: 'NaturaSì: -20% potenziale',
  reason: 'NaturaSì e BioMarket hanno meno riscatti rispetto al potenziale. Valuta campagne promozionali mirate per questi partner.',
  ctaLabel: 'Pianifica campagna'
}, {
  id: '3',
  title: 'Trend positivo sui workshop',
  type: 'Trend',
  diamondRange: '+35% engagement',
  reason: 'I workshop di sostenibilità hanno un alto valore in punti e buon engagement. Considera di espandere questa categoria.',
  ctaLabel: 'Esplora opportunità'
}];
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg" data-id="element-3179">
        <p className="font-semibold text-gray-900 mb-1" data-id="element-3180">
          {label || payload[0].name}
        </p>
        <p className="text-sm font-normal text-gray-600" data-id="element-3181">
          Riscatti:{' '}
          <span className="font-medium text-gray-900" data-id="element-3182">{payload[0].value}</span>
        </p>
      </div>;
  }
  return null;
};
const MarketplaceOverview = () => {
  const navigate = useNavigate();
  const [partnerFilter, setPartnerFilter] = useState('Tutti');
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  const filteredClaims = partnerFilter === 'Tutti' ? topClaims : topClaims.filter(claim => claim.partner === partnerFilter);
  const recommendations = getMarketplaceRecommendations().map(rec => ({
    ...rec,
    onCtaClick: () => console.log('CTA clicked:', rec.id)
  }));
  const handlePartnerClick = (e: React.MouseEvent, partnerId: string) => {
    e.stopPropagation();
    navigate('/partners');
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-3183">
      <div className="w-20 flex-shrink-0" data-id="element-3184">
        <Sidebar data-id="element-3185" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-3186">
        <Topbar data-id="element-3187" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-3188">
          {/* Page Header with AI Button */}
          <div className="mb-8" data-id="element-3189">
            <div className="flex items-start justify-between" data-id="element-3190">
              <div data-id="element-3191">
                <h1 className="text-xl font-semibold text-gray-900" data-id="element-3192">
                  Panoramica Marketplace
                </h1>
                <p className="text-sm font-normal text-gray-500 mt-1" data-id="element-3193">
                  Analisi riscatti, premi e performance dei partner
                </p>
              </div>
              <AIOutlineButton onClick={() => setIsAISidebarOpen(true)} data-id="element-3194">
                Suggerimenti AI
              </AIOutlineButton>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3195">
            <MetricCard title="Partners Attivi" value="28" change="8.2%" isPositive={true} icon={Building2} data-id="element-3196" />
            <MetricCard title="Punti Totali Riscattati" value="892,340" change="18.7%" isPositive={true} icon={Coins} data-id="element-3197" />
            <MetricCard title="Riscatti Totali" value="817" change="15.3%" isPositive={true} icon={TrendingDown} data-id="element-3198" />
          </div>

          {/* Charts Row */}
          <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-2" data-id="element-3199">
            {/* Claims by Partner */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-3200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3201">
                Distribuzione Riscatti per Partner
              </h3>
              <div className="h-80" data-id="element-3202">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3203">
                  <PieChart data-id="element-3204">
                    <Pie data={claimsByPartner} cx="50%" cy="50%" labelLine={false} label={({
                    name,
                    percent
                  }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={100} fill="#8884d8" dataKey="claims" data-id="element-3205">
                      {claimsByPartner.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-3206" />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip data-id="element-3208" />} data-id="element-3207" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Claims by Partner Bar Chart */}
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" data-id="element-3209">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3210">
                Riscatti per Partner
              </h3>
              <div className="h-80" data-id="element-3211">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3212">
                  <BarChart data={claimsByPartner} data-id="element-3213">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-3214" />
                    <XAxis dataKey="name" stroke="#6B7280" fontSize={12} data-id="element-3215" />
                    <YAxis stroke="#6B7280" fontSize={12} data-id="element-3216" />
                    <Tooltip content={<CustomTooltip data-id="element-3218" />} cursor={{
                    fill: 'rgba(229, 231, 235, 0.3)'
                  }} data-id="element-3217" />
                    <Bar dataKey="claims" radius={[4, 4, 0, 0]} data-id="element-3219">
                      {claimsByPartner.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-3220" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Top Claims Table */}
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-id="element-3221">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-3222">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-3223">
                Top Riscatti
              </h3>
              <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-3224">
                <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-3225" />
                <select className="bg-transparent text-sm font-normal focus:outline-none" value={partnerFilter} onChange={e => setPartnerFilter(e.target.value)} data-id="element-3226">
                  <option value="Tutti" data-id="element-3227">Tutti i Partners</option>
                  <option value="La Saponaria" data-id="element-3228">La Saponaria</option>
                  <option value="EcoStore" data-id="element-3229">EcoStore</option>
                  <option value="GreenLife" data-id="element-3230">GreenLife</option>
                  <option value="BioMarket" data-id="element-3231">BioMarket</option>
                  <option value="NaturaSì" data-id="element-3232">NaturaSì</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-3233">
              <table className="w-full" data-id="element-3234">
                <thead data-id="element-3235">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3236">
                    <th className="px-6 py-3" data-id="element-3237">Premio</th>
                    <th className="px-6 py-3" data-id="element-3238">Partner</th>
                    <th className="px-6 py-3" data-id="element-3239">Riscatti</th>
                    <th className="px-6 py-3" data-id="element-3240">Punti Totali</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-3241">
                  {filteredClaims.map(claim => <tr key={claim.id} className="hover:bg-gray-50" data-id="element-3242">
                      <td className="px-6 py-4" data-id="element-3243">
                        <div className="font-medium text-gray-900" data-id="element-3244">
                          {claim.reward}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-3245">
                        <button onClick={e => handlePartnerClick(e, claim.partnerId)} className="text-sm font-medium text-greenback hover:text-greenback/80 underline decoration-greenback decoration-2 underline-offset-2 transition-colors" data-id="element-3246">
                          {claim.partner}
                        </button>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3247">
                        {claim.claims}
                      </td>
                      <td className="px-6 py-4 font-medium text-green-600" data-id="element-3248">
                        {claim.points.toLocaleString()} punti
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* AI Sidebar */}
      <AISidebar isOpen={isAISidebarOpen} onClose={() => setIsAISidebarOpen(false)} title="Suggerimenti AI: Marketplace" data-id="element-3249">
        <AIRecommendationPanel recommendations={recommendations} data-id="element-3250" />
      </AISidebar>
    </div>;
};
export default MarketplaceOverview;